import { Doughnut } from 'react-chartjs-2';
import { useTheme } from 'styled-components';
import S from './DoghnutChartBigWidgetStyles';
import { ThemeType } from '../../../theme/themeType';
import { InfoCardProps } from '../../InfoCard/InfoCardProps';
import InfoCardHorizontal from '../../InfoCardHorizontal/InfoCardHorizontal';
import GlobalSpinner from '../GlobalSpinner/GlobalSpinner';

const CUTOUT_RADIUS = 25;
const BORDER_WIDTH = 4;
const CHART_SIZE = 260;

type Props = {
  data: {
    cards: Record<string, InfoCardProps>;
  }
  percentage?: boolean,
  loading?: boolean;
  fourRows?: boolean;
  emptyText?: string;
};

function DoghnutChartBigWidget({
  data, percentage, loading = false, fourRows = false, emptyText = 'No data',
}: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const values = Object.values(data.cards).map((card) => card.count);
  const total = values.reduce(
    (sum: number, currentValue: number | undefined) => sum + (currentValue || 0),
    0,
  );

  const chartConfig = {
    datasets: [
      {
        data: values,
        backgroundColor: Object.values(data.cards).map((card) => card.color),
        borderColor: theme.colors.widget_background,
        borderWidth: BORDER_WIDTH,
        cutout: CUTOUT_RADIUS,
      },
    ],
  };

  const options = {
    responsive: true,
    tooltips: {
      mode: 'label',
    },
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          label: (value: any) => {
            if (percentage && !!value.raw) {
              return `${Math.round((value.raw * 10000) / total) / 100}%`;
            }
            return value.raw;
          },
        },
      },
    },
  };

  if (loading) {
    return (<GlobalSpinner />);
  }

  return (
    <S.Wrapper>
      <div className="widget-container">
        <div className="info-cards">
          {Object.values(data.cards).map((item: InfoCardProps) => (
            <InfoCardHorizontal key={item.label} data={item} thin={fourRows} />
          ))}
        </div>
        <div className="chart-section">
          <div className="chart-container">
            {total === 0 ? (
              <div
                style={{
                  width: CHART_SIZE,
                  height: CHART_SIZE,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                  fontSize: 14,
                }}
              >
                {emptyText}
              </div>
            ) : (
              <Doughnut height={CHART_SIZE} width={CHART_SIZE} data={chartConfig} options={options} />
            )}
          </div>
          <div className="total-label">
            <span className="total-text">Total: </span>
            <span className="total-value">{total}</span>
          </div>
        </div>
      </div>
    </S.Wrapper>
  );
}

export default DoghnutChartBigWidget;
